import { useCallback } from 'react';
import {
  KoButton,
  KoForm,
  KoFormBuilder,
  KoSelect,
  KoDate,
  KoInput,
  KoTextArea,
  KoWorkflow
} from 'packages';
import KoIcon from '@kwapio/icons';
import {
  RELEASE_CLASSIFICATION,
  ENVIRONMENT_OPTIONS,
  RELEASE_TEAM_OPTIONS,
  RISK_LEVEL_OPTIONS,
  MIGRATION_OPTIONS,
  DEFAULT_RELEASE_FLOW
} from '../../constants';
import styles from './crud.module.scss';

function ReleaseCrud({ form, handleFinish, initialValues }) {
  const handleSubmit = useCallback((values) => {
    handleFinish(values);
  }, [handleFinish]);

  const ClassificationWidget = ({ value, onChange }) => (
    <KoSelect
      value={value}
      onChange={(v) => onChange(v)}
      placeholder='Select release classification'
      options={RELEASE_CLASSIFICATION}
    />
  );

  const EnvWidget = ({ value, onChange }) => (
    <KoSelect
      value={value}
      onChange={(v) => onChange(v)}
      placeholder='Select target environment'
      options={ENVIRONMENT_OPTIONS}
    />
  );

  const DateWidget = ({ value, onChange }) => (
    <KoDate
      value={value}
      onChange={(v) => onChange(v)}
      style={{ width: '100%' }}
      placeholder='Select target date'
    />
  );

  const RiskWidget = ({ value, onChange }) => (
    <KoSelect
      value={value}
      onChange={(v) => onChange(v)}
      placeholder='Risk level'
      options={RISK_LEVEL_OPTIONS}
    />
  );

  const MigrationWidget = ({ value, onChange }) => (
    <KoSelect
      value={value}
      onChange={(v) => onChange(v)}
      placeholder='Migration classification'
      options={MIGRATION_OPTIONS}
    />
  );

  const ReleaseFlowWidget = ({ value, onChange }) => {
    const flowData = {
      name: 'Release Flow',
      workflow: Array.isArray(value) && value.length > 0 ? value : DEFAULT_RELEASE_FLOW
    };

    const handleFlowChange = (template) => {
      onChange(template.workflow);
    };

    const handleReset = () => {
      onChange([...DEFAULT_RELEASE_FLOW]);
    };

    return (
      <div className={styles.flowWidget}>
        <KoWorkflow
          noend
          data={flowData}
          onChange={handleFlowChange}
          resetWorkflow={handleReset}
        />
      </div>
    );
  };

  const ParticipatingTeamsWidget = ({ value, onChange }) => {
    const teams = Array.isArray(value) ? value : [];
    const assignedTeamValues = teams.map(t => t.team).filter(Boolean);
    const availableTeams = RELEASE_TEAM_OPTIONS.filter(o => !assignedTeamValues.includes(o.value));

    const addTeam = () => {
      onChange([...teams, { team: '', branch: '' }]);
    };

    const removeTeam = (index) => {
      onChange(teams.filter((_, i) => i !== index));
    };

    const updateTeam = (index, field, val) => {
      onChange(teams.map((t, i) => i === index ? { ...t, [field]: val } : t));
    };

    return (
      <div className={styles.teamsWidget}>
        {teams.length > 0 && (
          <div className={styles.teamRowHeader}>
            <span style={{ flex: 1 }}>Team</span>
            <span style={{ flex: 1 }}>Release Branch</span>
            <span style={{ width: 32 }} />
          </div>
        )}
        {teams.map((entry, idx) => (
          <div key={idx} className={styles.teamRow}>
            <KoSelect
              value={entry.team || undefined}
              onChange={(v) => updateTeam(idx, 'team', v)}
              placeholder='Select team'
              options={[
                ...availableTeams,
                ...(entry.team ? RELEASE_TEAM_OPTIONS.filter(o => o.value === entry.team) : [])
              ]}
              style={{ flex: 1 }}
            />
            <KoInput
              value={entry.branch}
              onChange={(e) => updateTeam(idx, 'branch', e.target.value)}
              placeholder='e.g. release/api-v3.0'
              style={{ flex: 1, fontFamily: 'var(--font-family-mono, monospace)' }}
            />
            <KoButton
              iconBtn
              icon={<KoIcon name='DeleteOutlined' width={14} height={14} color='var(--palette-error-main)' />}
              onClick={() => removeTeam(idx)}
            />
          </div>
        ))}
        <KoButton
          label='Add Team'
          type='text'
          icon={<KoIcon name='PlusOutlined' width={14} height={14} />}
          onClick={addTeam}
          disabled={availableTeams.length === 0}
          style={{ alignSelf: 'flex-start' }}
        />
      </div>
    );
  };

  const meta = {
    columns: 4,
    formItemLayout: null,
    initialValues,
    colon: true,
    fields: [
      {
        key: 'name',
        label: 'Release Name',
        colSpan: 2,
        widget: KoInput,
        widgetProps: { placeholder: 'e.g. Platform Upgrade, Auth Redesign' },
        required: true,
        rules: [{ required: true, message: 'Release name is required' }]
      },
      {
        key: 'version',
        label: 'Version (SemVer)',
        colSpan: 1,
        widget: KoInput,
        widgetProps: {
          placeholder: 'v4.2.0',
          style: { fontFamily: 'var(--font-family-mono, monospace)' }
        },
        required: true,
        rules: [{ required: true, message: 'Version is required' }]
      },
      {
        key: 'classification',
        label: 'Classification',
        colSpan: 1,
        widget: ClassificationWidget,
        forwardRef: true,
        required: true,
        rules: [{ required: true, message: 'Classification is required' }]
      },
      {
        key: 'environment',
        label: 'Target Environment',
        colSpan: 2,
        widget: EnvWidget,
        forwardRef: true
      },
      {
        key: 'targetDate',
        label: 'Target Release Date',
        colSpan: 2,
        widget: DateWidget,
        forwardRef: true
      },
      {
        key: 'participatingTeams',
        label: 'Participating Teams & Release Branches',
        colSpan: 4,
        widget: ParticipatingTeamsWidget,
        forwardRef: true
      },
      {
        key: 'releaseFlow',
        label: 'Release Flow',
        colSpan: 4,
        widget: ReleaseFlowWidget,
        forwardRef: true
      },
      {
        key: 'owner',
        label: 'Release Owner',
        colSpan: 2,
        widget: KoInput,
        widgetProps: { placeholder: 'Owner name or email' }
      },
      {
        key: 'riskLevel',
        label: 'Risk Level',
        colSpan: 2,
        widget: RiskWidget,
        forwardRef: true
      },
      {
        key: 'migrationClassification',
        label: 'Migration Classification',
        colSpan: 2,
        widget: MigrationWidget,
        forwardRef: true
      },
      {
        key: 'rollbackStrategy',
        label: 'Rollback Strategy',
        colSpan: 4,
        widget: KoTextArea,
        widgetProps: {
          placeholder: 'Describe the rollback plan in case of failure...',
          rows: 2
        }
      },
      {
        key: 'featureFlagPlan',
        label: 'Feature Flag Plan',
        colSpan: 4,
        widget: KoTextArea,
        widgetProps: {
          placeholder: 'Which features are behind flags? Default states? Gradual rollout strategy?',
          rows: 2
        }
      },
      {
        key: 'description',
        label: 'Description / Goals',
        colSpan: 4,
        widget: KoTextArea,
        widgetProps: {
          placeholder: 'What is the goal of this release? What stakeholders are involved?',
          rows: 3
        }
      }
    ]
  };

  return (
    <div className={styles.wrapper}>
      <KoForm
        form={form}
        layout='vertical'
        onFinish={handleSubmit}
        onSubmit={handleSubmit}
      >
        <KoFormBuilder form={form} meta={meta} />
      </KoForm>
    </div>
  );
}

export default ReleaseCrud;
